import React, { useState, useEffect, useContext } from 'react';
import { Spin } from 'antd';
import { v4 as uuid } from 'uuid';
import { format } from 'date-fns';

import { GlobalContext } from '../../context/GlobalContext';
import getSitesForOrg from '../../services/getSitesForOrg';
import uploadFileListToS3 from '../../services/uploadFileListToS3';
import { Button, UploadTarget } from '../../components';
import UploadedFileTable from './UploadedFileTable';
import useUploadStatusMap from '../../hooks/useUploadStatusMap';
import {
  ButtonDiv,
  ButtonGroup,
  StyledUpload,
  StyledModalDiv,
  StyledHeader,
  StyledFileTable,
  StyledPlaceholderDiv
} from './styled-components';

const formFieldData = {
  key: null,
  displayName: '',
  inspectionId: null,
  sensorType: null,
  fileType: null,
  orgId: null,
  siteId: null,
  flogistixId: null,
  isRowValid: false,
  uploadStatus: null,
  sites: [],
  identifiers: []
};

const Upload = ({
  selectedInspection = null,
  selectedSite = null,
  selectedOrg = null,
  inModal
}) => {
  const {
    token,
    inspections,
    user_organizations,
    loading,
    netsuiteToAxilSiteIdMap,
    netsuiteToAxilCustomerIdMap,
    getAndSetInspections,
    inspectionsReloadIsPending
  } = useContext(GlobalContext);

  const {
    uploadStatusMap,
    uploadingFiles,
    setUploadingFiles,
    setAllFilesToPending,
    uploadingFileFailed,
    uploadingFileSuccess,
    resetUpload
  } = useUploadStatusMap();

  const statusMap = uploadStatusMap;
  const uploadedFiles = uploadingFiles;
  const setUploadedFiles = setUploadingFiles;
  const onFilesPending = setAllFilesToPending;
  const onFileUploadFailed = uploadingFileFailed;
  const onFileUploadSuccess = uploadingFileSuccess;

  const [canSubmit, setCanSubmit] = useState(false);
  const uploadQueueIsActive = Object.entries(statusMap).length > 0;
  const [dropdownInspections, setDropdownInspections] = useState(inspections);

  const getValidatedDataItem = (dataItem) => {
    const {
      displayName,
      inspectionId,
      sensorType,
      orgId,
      siteId,
      flogistixId
    } = dataItem;

    const isRowValid = displayName?.length > 0
            && ![inspectionId, sensorType, orgId, siteId, flogistixId].includes(
              null
            );

    return {
      ...dataItem,
      isRowValid
    };
  };

  const getUploadStatusByFileKey = (data) => statusMap?.[data?.key];

  const updateUploadedFile = (data, updatedPropContainer) => setUploadedFiles(
    uploadedFiles.map((file) => (file.key === data.key
      ? getValidatedDataItem({ ...file, ...updatedPropContainer })
      : file))
  );

  const isFormValid = () => uploadedFiles?.length > 0
        && uploadedFiles.every(({ isRowValid }) => isRowValid)
        && !uploadQueueIsActive
        && !inspectionsReloadIsPending;

  const removeUploadedFile = (data) => {
    setUploadedFiles(uploadedFiles.filter((file) => file.key !== data.key));
  };

  const addFilesToQueue = (newFiles) => {
    const newDataItems = newFiles.map((file) => ({
      ...formFieldData,
      fileName: file.name,
      displayName: file.name,
      key: uuid(),
      file,
      ...(!!selectedInspection && {
        inspectionId: selectedInspection?.id,
        orgId: netsuiteToAxilCustomerIdMap?.[
          selectedInspection?.customerId
        ],
        siteId: netsuiteToAxilSiteIdMap?.[
          selectedInspection?.customerLocationId
        ],
        flogistixId: selectedInspection?.flogistixId
      })
    }));

    setUploadedFiles([...uploadedFiles, ...newDataItems]);
  };

  const handleFileDrop = (droppedFiles) => {
    if (uploadQueueIsActive) {
      return;
    }

    addFilesToQueue(droppedFiles);
  };

  const handleInspectionChange = (data, inspection) => {
    const {
      flogistixId,
      id: inspectionId,
      customerLocationId,
      customerId
    } = inspection;
    const siteId = netsuiteToAxilSiteIdMap?.[customerLocationId];
    const orgId = netsuiteToAxilCustomerIdMap?.[customerId];

    updateUploadedFile(data, {
      orgId,
      siteId,
      inspectionId,
      flogistixId
    });
  };

  const handleOrgChange = (data, org) => {
    const { netsuiteId } = org;

    const orgId = netsuiteToAxilCustomerIdMap?.[netsuiteId];

    getSitesForOrg(orgId, data, updateUploadedFile, token);
  };

  const handleSiteChange = (data, site) => {
    const { netsuiteId } = site;

    const siteId = netsuiteToAxilSiteIdMap?.[netsuiteId];

    const filteredInspections = inspections.filter(
      (inspection) => inspection.customerLocationId === netsuiteId
    );
    setDropdownInspections(filteredInspections);

    const formattedIdentifierOptions = filteredInspections?.map(
      (inspection) => ({
        label: `${format(
          new Date(inspection.inspectionDate),
          'MM/dd/yy'
        )} ${inspection.flogistixId}`,
        value: inspection
      })
    );

    updateUploadedFile(data, {
      siteId,
      identifiers: formattedIdentifierOptions
    });
  };

  const handleDeleteFile = (data) => {
    removeUploadedFile(data);
  };

  const handleFileTypeChange = (data, fileTypeId) => {
    updateUploadedFile(data, { fileType: fileTypeId });
  };

  const handleSensorTypeChange = (data, sensorTypeId) => {
    updateUploadedFile(data, { sensorType: sensorTypeId });
  };

  const handleDisplayNameChange = (data, displayName) => {
    updateUploadedFile(data, { displayName });
  };

  const handleCancel = () => {
    resetUpload();
  };

  const handleSubmit = async () => {
    await uploadFileListToS3({
      uploadedFiles,
      onFilesPending,
      onFileUploadSuccess,
      onFileUploadFailed,
      token
    });
    await getAndSetInspections();
  };

  useEffect(() => {
    setCanSubmit(isFormValid());
  }, [uploadedFiles, statusMap, inspectionsReloadIsPending, canSubmit]);

  return (
    <StyledUpload height={!inModal ? '90vh' : 'auto'}>
      {!inModal && <StyledHeader>Multi-File Upload</StyledHeader>}
      {loading ? (
        <Spin />
      ) : (
        <div>
          <UploadTarget
            onFileDrop={handleFileDrop}
            disabled={uploadQueueIsActive}
          />
          {uploadedFiles?.length > 0 ? (
            <div>
              {inModal ? (
                <StyledModalDiv>
                  <UploadedFileTable
                    uploadedFiles={uploadedFiles}
                    onSensorTypeChange={
                      handleSensorTypeChange
                    }
                    onFileTypeChange={handleFileTypeChange}
                    onDeleteFile={handleDeleteFile}
                    onInspectionChange={
                      handleInspectionChange
                    }
                    onSiteChange={handleSiteChange}
                    onOrgChange={handleOrgChange}
                    onDisplayNameChange={
                      handleDisplayNameChange
                    }
                    inspections={dropdownInspections}
                    orgs={user_organizations}
                    selectedInspection={selectedInspection}
                    selectedSite={selectedSite}
                    selectedOrg={selectedOrg}
                    statusMap={statusMap}
                    getUploadStatusByFileKey={
                      getUploadStatusByFileKey
                    }
                  />
                  <ButtonGroup>
                    <ButtonDiv>
                      <Button
                        nospin="true"
                        variant="outline"
                        handleClick={handleCancel}
                      >
                        Clear
                      </Button>
                    </ButtonDiv>
                    <ButtonDiv>
                      <Button
                        nospin="true"
                        isDisabled={!canSubmit}
                        handleClick={handleSubmit}
                      >
                        Save
                      </Button>
                    </ButtonDiv>
                  </ButtonGroup>
                </StyledModalDiv>
              ) : (
                <div>
                  <StyledFileTable>
                    <UploadedFileTable
                      statusMap={statusMap}
                      orgs={user_organizations}
                      selectedOrg={selectedOrg}
                      selectedSite={selectedSite}
                      uploadedFiles={uploadedFiles}
                      onOrgChange={handleOrgChange}
                      onDeleteFile={handleDeleteFile}
                      onSiteChange={handleSiteChange}
                      inspections={dropdownInspections}
                      selectedInspection={
                        selectedInspection
                      }
                      onFileTypeChange={
                        handleFileTypeChange
                      }
                      onInspectionChange={
                        handleInspectionChange
                      }
                      onSensorTypeChange={
                        handleSensorTypeChange
                      }
                      onDisplayNameChange={
                        handleDisplayNameChange
                      }
                      getUploadStatusByFileKey={
                        getUploadStatusByFileKey
                      }
                    />
                  </StyledFileTable>
                  <ButtonGroup>
                    <ButtonDiv>
                      <Button
                        nospin="true"
                        variant="outline"
                        handleClick={handleCancel}
                      >
                        Clear
                      </Button>
                    </ButtonDiv>
                    <ButtonDiv>
                      <Button
                        nospin="true"
                        isDisabled={!canSubmit}
                        handleClick={handleSubmit}
                      >
                        Save
                      </Button>
                    </ButtonDiv>
                  </ButtonGroup>
                </div>
              )}
            </div>
          ) : (
            <div>
              {!inModal && <StyledPlaceholderDiv />}
              <ButtonGroup>
                <ButtonDiv>
                  <Button
                    nospin="true"
                    variant="outline"
                    handleClick={handleCancel}
                  >
                    Clear
                  </Button>
                </ButtonDiv>
                <ButtonDiv>
                  <Button
                    asyncClick
                    nospin="true"
                    isDisabled={!canSubmit}
                    handleClick={handleSubmit}
                  >
                    Save
                  </Button>
                </ButtonDiv>
              </ButtonGroup>
            </div>
          )}
        </div>
      )}
    </StyledUpload>
  );
};

export default Upload;
